import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from "@angular/core";

import IUserSkill from "../../../models/userSkills";
import ISkillNodeWithRating from "@skills-app/models/skillNodeWithRating";
import { ISkillNode } from "@skills-app/models/skillNode";
import { OrderSkillsByProficiencyThenNamePipe } from "@skills-app/shared/pipes/order-skills-by-proficiency-then-name.pipe";

@Component({
  standalone: false,
  selector: "app-consultant-card",
  templateUrl: "./consultant-card.component.html",
  styleUrls: ["./consultant-card.component.css"],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [OrderSkillsByProficiencyThenNamePipe],
})
export class ConsultantCardComponent implements OnChanges {
  @Input() azureId!: string;
  @Input() consultant!: IUserSkill;

  @Input() matchedSkillIds: Array<string | number> = [];

  @Input() isEditEnabled = false;
  @Input() isExpandEnabled = true;
  @Input() isSelectEnabled = false;
  @Input() enableTeamToggle = false;
  @Input() maxCollapsedSkillsToDisplay = 12;
  @Input() elevatedSkills: ISkillNode[] = [];

  @Output() skillChipClicked = new EventEmitter<ISkillNodeWithRating>();
  @Output() consultantEditClicked = new EventEmitter<IUserSkill>();
  @Output() consultantInfoClicked = new EventEmitter<IUserSkill>();
  @Output() selectConsultantClicked = new EventEmitter<IUserSkill>();
  @Output() viewProfileClick = new EventEmitter<IUserSkill>();

  sortedSkills: ISkillNodeWithRating[] = [];
  skillsToDisplay: ISkillNodeWithRating[] = [];

  constructor(
    private readonly skillSorter: OrderSkillsByProficiencyThenNamePipe,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["consultant"]) {
      this.recomputeSortedSkills();
      this.updateSkillsToDisplay();
    }
  }

  private getConsultantMappedSkills(): ISkillNodeWithRating[] {
    const c: any = this.consultant as any;
    if (!c) return [];

    const candidates = [
      c.MappedSkills,
      c.mappedSkills,
      c.Skills,
      c.skills,
      c.UserSkills,
      c.userSkills,
    ];

    for (const v of candidates) {
      if (Array.isArray(v) && v.length > 0) return v;
    }

    if (Array.isArray(c.MappedSkills)) return c.MappedSkills;
    if (Array.isArray(c.Skills)) return c.Skills;
    if (Array.isArray(c.UserSkills)) return c.UserSkills;

    return [];
  }

  private recomputeSortedSkills(): void {
    const mapped = this.getConsultantMappedSkills();
    this.sortedSkills = this.skillSorter.transform(mapped ?? []);
  }

  updateSkillsToDisplay(): void {
    this.skillsToDisplay = this.sortedSkills.slice(
      0,
      this.maxCollapsedSkillsToDisplay,
    );
  }

  openPreview(event?: Event): void {
    if (event) event.stopPropagation();
    if (!this.consultant?.HasProfile) return;
    this.viewProfileClick.emit(this.consultant);
  }

  editConsultantSkills(event?: Event): void {
    if (event) event.stopPropagation();
    this.consultantEditClicked.emit(this.consultant);
  }

  showConsultantInformation(event?: Event): void {
    if (event) event.stopPropagation();
    this.consultantInfoClicked.emit(this.consultant);
  }

  consultantSelected(): void {
    this.selectConsultantClicked.emit(this.consultant);
  }

  sendEmail(event?: Event): void {
    if (event) event.stopPropagation();

    const email = this.consultant?.Email;
    if (!email) return;

    window.location.href = "mailto:" + email;
  }

  onSkillChipClicked(payload: [ISkillNodeWithRating, boolean]): void {
    const skill = payload?.[0];
    if (!skill) return;
    this.skillChipClicked.emit(skill);
  }
}
